import { catalog, getItem, QUALITIES, type Quality } from '@wyn/content';
import type { TradeBundleView, TradeOfferView } from '@wyn/contracts';
import { useQuery } from '@tanstack/react-query';
import { Navigate } from '@tanstack/react-router';
import { useState } from 'react';
import { GameShell } from '../components/Layout.tsx';
import { ErrorNotice } from '../components/Notice.tsx';
import { ReportButton } from '../components/Report.tsx';
import { Callout, PageTitle } from '../components/ui.tsx';
import { UI } from '../icons/ui-icons.ts';
import { offersQuery, useAction, useDeclineTrade } from '../lib/queries.ts';
import { formatDuration, useNow } from '../lib/time.ts';
import { useMe } from '../lib/useMe.ts';
import { Loading } from './Landing.tsx';

const ITEMS = [...catalog.items].sort((a, b) => a.name.localeCompare(b.name));
type Line = TradeBundleView['items'][number];

const describe = (b: TradeBundleView) =>
  [...(b.coin ? [`${b.coin} coin`] : []), ...b.items.map((l) => `${l.quantity} × ${l.quality} ${getItem(l.itemKey).name.toLowerCase()}`)].join(', ') || 'nothing';

/** Direct trades: offer someone coin and goods for theirs. Both sides move at once when they accept. */
export function Trades() {
  const me = useMe();
  if (me.isPending) return <Loading />;
  if (!me.data?.player) return <Navigate to="/" />;
  return (
    <GameShell me={me.data} skew={me.skew}>
      <div className="space-y-6">
        <section>
          <PageTitle icon={UI.trades}>Trades</PageTitle>
          <p className="text-ink-dim">Strike a bargain with anyone in the realm. Nothing changes hands until they accept; offers lapse after a day.</p>
        </section>
        <Offers />
        <NewOffer />
      </div>
    </GameShell>
  );
}

function Offers() {
  const q = useQuery(offersQuery());
  const accept = useAction('acceptTrade', { refetch: ['offers', 'me'] });
  const decline = useDeclineTrade();
  const now = useNow();
  const skew = useMe().skew;
  const row = (o: TradeOfferView, incoming: boolean) => (
    <li key={o.id} className="flex flex-wrap items-center justify-between gap-2 border-b border-rule/60 pb-2 last:border-0">
      <div className="min-w-0 text-sm">
        {incoming ? (
          <><span className="text-ink">{o.fromName}</span> offers {describe(o.give)} for {describe(o.want)}.</>
        ) : (
          <>You offer <span className="text-ink">{o.toName}</span> {describe(o.give)} for {describe(o.want)}.</>
        )}
        <div className="font-mono text-xs text-ink-faint">lapses in {formatDuration(o.expiresAt - (now + skew))}</div>
      </div>
      <div className="flex items-center gap-2">
        <ReportButton target={{ kind: 'player', name: incoming ? o.fromName : o.toName }} what={incoming ? o.fromName : o.toName} className="mr-1" />
        {incoming && (
          <button type="button" className="btn" disabled={accept.isPending} onClick={() => accept.mutate({ offerId: o.id })}>
            Accept
          </button>
        )}
        <button type="button" className="btn-quiet" disabled={decline.isPending} onClick={() => decline.mutate(o.id)}>
          {incoming ? 'Decline' : 'Withdraw'}
        </button>
      </div>
    </li>
  );
  return (
    <section className="panel space-y-3">
      <h2 className="heading">Offers</h2>
      {(q.data?.incoming.length ?? 0) + (q.data?.outgoing.length ?? 0) === 0 ? (
        <p className="text-sm text-ink-faint">{q.isPending ? 'Fetching the post…' : 'Nothing waiting.'}</p>
      ) : (
        <ul className="space-y-2">
          {q.data!.incoming.map((o) => row(o, true))}
          {q.data!.outgoing.map((o) => row(o, false))}
        </ul>
      )}
      {accept.data && !accept.isPending && <Callout tone="good" role="status">Done. The goods have changed hands.</Callout>}
      <ErrorNotice error={accept.error ?? decline.error ?? q.error} />
    </section>
  );
}

function BundleEditor({ label, value, onChange, choices }: { label: string; value: TradeBundleView; onChange: (b: TradeBundleView) => void; choices: 'pack' | 'all' }) {
  const inv = useMe().data!.inventory;
  const [itemKey, setItemKey] = useState(choices === 'pack' ? (inv[0] ? `${inv[0].itemKey}#${inv[0].quality}` : '') : `${ITEMS[0]!.key}#common`);
  const [qty, setQty] = useState(1);
  const [key, quality] = itemKey.split('#') as [string, Quality];
  const add = () => {
    if (!key) return;
    const rest = value.items.filter((l) => !(l.itemKey === key && l.quality === quality));
    onChange({ ...value, items: [...rest, { itemKey: key, quality, quantity: qty } satisfies Line] });
  };
  return (
    <fieldset className="space-y-2 border border-rule p-3">
      <legend className="smallcaps px-1 text-sm text-gilt">{label}</legend>
      <label className="flex items-center gap-2 text-sm">
        <span className="text-ink-dim">Coin</span>
        <input className="field w-28 font-mono" type="number" min={0} aria-label={`${label} coin`} value={value.coin} onChange={(e) => onChange({ ...value, coin: Math.max(0, Math.floor(Number(e.target.value) || 0)) })} />
      </label>
      <div className="flex flex-wrap items-center gap-2 text-sm">
        {choices === 'pack' ? (
          <select className="field" aria-label={`${label} good`} value={itemKey} onChange={(e) => setItemKey(e.target.value)}>
            {inv.map((i) => (
              <option key={`${i.itemKey}#${i.quality}`} value={`${i.itemKey}#${i.quality}`}>
                {getItem(i.itemKey).name} ({i.quality}, ×{i.quantity})
              </option>
            ))}
          </select>
        ) : (
          <>
            <select className="field" aria-label={`${label} good`} value={key} onChange={(e) => setItemKey(`${e.target.value}#${quality}`)}>
              {ITEMS.map((i) => <option key={i.key} value={i.key}>{i.name}</option>)}
            </select>
            <select className="field" aria-label={`${label} quality`} value={quality} onChange={(e) => setItemKey(`${key}#${e.target.value}`)}>
              {QUALITIES.map((x) => <option key={x} value={x}>{x}</option>)}
            </select>
          </>
        )}
        <input className="field w-20 font-mono" type="number" min={1} aria-label={`${label} quantity`} value={qty} onChange={(e) => setQty(Math.max(1, Math.floor(Number(e.target.value) || 1)))} />
        <button type="button" className="btn-quiet" disabled={!key} onClick={add}>add</button>
      </div>
      {value.items.length > 0 && (
        <ul className="space-y-1 text-sm">
          {value.items.map((l) => (
            <li key={`${l.itemKey}#${l.quality}`} className="flex justify-between gap-2">
              <span>{l.quantity} × {l.quality} {getItem(l.itemKey).name.toLowerCase()}</span>
              <button type="button" className="text-xs text-ink-faint hover:text-ink" onClick={() => onChange({ ...value, items: value.items.filter((x) => x !== l) })}>remove</button>
            </li>
          ))}
        </ul>
      )}
    </fieldset>
  );
}

const EMPTY: TradeBundleView = { coin: 0, items: [] };

function NewOffer() {
  const propose = useAction('proposeTrade', { refetch: ['offers'] });
  const [to, setTo] = useState('');
  const [give, setGive] = useState<TradeBundleView>(EMPTY);
  const [want, setWant] = useState<TradeBundleView>(EMPTY);
  return (
    <section className="panel space-y-3">
      <h2 className="heading">Make an offer</h2>
      <form
        className="space-y-3"
        onSubmit={(e) => {
          e.preventDefault();
          propose.mutate({ to, give, want }, { onSuccess: () => { setGive(EMPTY); setWant(EMPTY); } });
        }}
      >
        <label className="flex items-center gap-2">
          <span className="text-sm text-ink-dim">To</span>
          <input className="field w-64" placeholder="Their name" aria-label="Trade with" value={to} onChange={(e) => setTo(e.target.value)} />
        </label>
        <div className="grid gap-3 md:grid-cols-2">
          <BundleEditor label="You give" value={give} onChange={setGive} choices="pack" />
          <BundleEditor label="You want" value={want} onChange={setWant} choices="all" />
        </div>
        <button className="btn" type="submit" disabled={propose.isPending || to.trim().length < 3}>Send offer</button>
      </form>
      {propose.data && !propose.isPending && <Callout tone="good" role="status">Offer sent.</Callout>}
      <ErrorNotice error={propose.error} />
    </section>
  );
}
